/**
 * One answer to "which copilot is mounted here?" for surfaces that hand it a
 * prompt (TASK-816).
 *
 * Two stores carry a handed-off prompt and each has exactly ONE consumer:
 * `canvasStore.pendingPrompt` → the canvas InputDock, mounted only where the
 * canvas owns the input; `dockStore.pendingPrompt` → the bottom Dock, which
 * unmounts wherever `isDockSuppressed`. Handing a prompt to the store whose
 * consumer is not mounted is a SILENT no-op: the button looks alive and
 * nothing happens — reported live on the onboarding checklist's "Prepare pack",
 * which always used `dockStore` while rendering on the dashboard canvas, where
 * that dock never mounts. Callers ask here instead of each re-deriving the rule.
 */
import { useCanvasStore } from '../../stores/canvasStore';
import { useDockStore } from '../../stores/dockStore';
import type { RequestedPrompt } from '../../stores/createChatStore';
import { isDockSuppressed } from './dockScope';

/** Whether a prompt handed over right now would actually be sent. */
export function hasCopilotPromptTarget(pathname: string): boolean {
  return (
    useCanvasStore.getState().promptConsumers > 0 ||
    !isDockSuppressed(pathname) ||
    pathname.startsWith('/dashboard')
  );
}

/**
 * Hand `prompt` to the mounted copilot. Returns false when neither consumer is
 * mounted (the workbench rail owns the input there and takes no handoff) so the
 * caller can say so rather than appear to have done something.
 */
export function requestCopilotPrompt(prompt: RequestedPrompt, pathname: string): boolean {
  // `promptConsumers` is the live fact "the canvas InputDock is mounted"
  // (TASK-768), not a route guess, so it is asked first.
  if (useCanvasStore.getState().promptConsumers > 0) {
    useCanvasStore.getState().requestPrompt(prompt);
    return true;
  }
  if (!isDockSuppressed(pathname)) {
    useDockStore.getState().requestPrompt(prompt);
    return true;
  }
  // On /dashboard the canvas InputDock IS the dock (TASK-712) even in the
  // instant before its consumer registration effect runs — `pendingPrompt`
  // waits in the store until it mounts, so this is a handoff, not a guess.
  if (pathname.startsWith('/dashboard')) {
    useCanvasStore.getState().requestPrompt(prompt);
    return true;
  }
  return false;
}
